import { Component, SanEvent } from 'san';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import * as _ from 'lodash';

import { EProjectsOrder, ESearchMode } from 'src/enums';
import { PROJECT_MORE_FILTERS_KEYS, ROOT_PARENT_ORG_NUMBER } from 'src/consts';
import {
    IOrganization,
    IProjectFilterItem,
    IProjectFilters,
    IProjectsParams,
    IProjectFilterOrganization,
    IProjectFilterCreateOrganization,
} from 'src/models';

import projectService from 'src/services/project.service';
import organizationService from 'src/services/organization.service';

import DatePicker from '../DatePicker/DatePicker';
import ProjectFiltersChecked from '../ProjectFiltersChecked/ProjectFiltersChecked';
import ProjectFilter from '../ProjectFilter/ProjectFilter';
import ProjectFilterLight from '../ProjectFilterLight/ProjectFilterLight';
import ProjectFilterOrganization from '../ProjectFilterOrganization/ProjectFilterOrganization';
import ProjectFilterCreateOrganization from '../ProjectFilterCreateOrganization/ProjectFilterCreateOrganization';

export default class ProjectFilters extends Component {
    constructor(options: any) {
        super(options);
    }

    static components = {
        'ui-date-picker': DatePicker,
        'ui-project-filters-checked': ProjectFiltersChecked,
        'ui-project-filter': ProjectFilter,
        'ui-project-filter-light': ProjectFilterLight,
        'ui-project-filter-organization': ProjectFilterOrganization,
        'ui-project-filter-create-organization': ProjectFilterCreateOrganization,
    };

    messages = {
        'UI:project-filter-changed': (
            arg: SanEvent<any, { key: string; items: IProjectFilterItem[] }>
        ) => {
            const dataKey = 'projectFilters.' + arg.value.key + '.items';
            const items = this.data.get(dataKey);
            this.data.splice(dataKey, [0, items.length, ...arg.value.items]);
            this.fireProjectsParams();
        },
        'UI:project-filter-organization-changed': (arg: SanEvent<any, IOrganization>) => {
            const organization = arg.value;
            this.toggleCheckProjectFilterOrganization(organization);
        },
        'UI:project-filter-create-organization-changed': (arg: SanEvent<any, IOrganization>) => {
            const organization = arg.value;
            this.toggleCheckProjectFilterCreateOrganization(organization);
        },
    };

    initData(): {
        projectFilters: IProjectFilters;
        projectFilterOrganization: IProjectFilterOrganization;
        projectFilterCreateOrganization: IProjectFilterCreateOrganization;
        checkedParams: IProjectsParams;
        isAll: boolean; // {{}} 显示所有筛选条件。 true: 项目中心项目列表页。 false: 选择项目列表组件CheckProjects
        isShowMore: boolean;
        projectsOrderEnum: any;
        searchQ$: Subject<string>;
        searchMode: ESearchMode; // 搜索模式：简约模式/完整树模式
    } {
        return {
            projectFilters: {},
            projectFilterOrganization: { title: '责任单位', items: [] },
            projectFilterCreateOrganization: { title: '创建单位', items: [] },
            checkedParams: {},
            isAll: false,
            isShowMore: false,
            projectsOrderEnum: EProjectsOrder,
            searchQ$: new Subject<string>(),
            searchMode: ESearchMode.Default,
        };
    }

    inited() {
        this.initFilters();
        this.data
            .get('searchQ$')
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(() => {
                this.search();
            });
        this.watch('q', (value) => {
            this.data.get('searchQ$').next(value);
        });
        this.watch('from_date', () => {
            this.search();
        });
        this.watch('to_date', () => {
            this.search();
        });
        this.watch('searchMode', () => {
            this.search();
        });
    }

    // 初始化选中状态
    initFilters() {
        const checkedParams: IProjectsParams = this.data.get('checkedParams');
        this.initIsShowMore(checkedParams);
        projectService.getProjectFilters().then((projectFilters) => {
            _.forEach(projectFilters, (filter, key) => {
                _.forEach(filter.items, (item) => {
                    item.is_checked = _.includes(checkedParams[key], item.value);
                });
            });
            const order = checkedParams.order;
            const orgNumber = checkedParams.organization;
            this.initProjectFilterOrganization(orgNumber);
            const createOrgNumber = checkedParams.create_organization;
            this.initProjectFilterCreateOrganization(createOrgNumber);
            this.data.set('order', order ? order : EProjectsOrder.Status); // 排序规则 默认为”项目状态“
            this.data.set('q', checkedParams.q);
            this.data.set('from_date', checkedParams.from_date);
            this.data.set('to_date', checkedParams.to_date);
            this.data.set('projectFilters', projectFilters);
        });
    }

    // 初始化”更多条件“展开状态
    initIsShowMore(checkedParams: IProjectsParams) {
        const keys = _.keys(checkedParams);
        const isShowMore = _.intersection(keys, PROJECT_MORE_FILTERS_KEYS).length;
        this.data.set('isShowMore', isShowMore);
    }

    // 初始化责任单位
    initProjectFilterOrganization(orgNumber?: string) {
        // 未选中，获取一级组织
        if (!orgNumber) {
            organizationService.getChildrenOrganizations().then((organizations) => {
                const dataKey = 'projectFilterOrganization.items';
                const items = this.data.get(dataKey);
                this.data.splice(dataKey, [0, items.length, ...organizations]);
            });
            return;
        }
        // 已选中，获取选中的组织，及其子组织
        let organization: IOrganization;
        organizationService
            .getOrganization(orgNumber)
            .then((org) => {
                org._is_checked = true;
                organization = org;
                return organizationService.getChildrenOrganizations(organization.parent_org_number);
            })
            .then((childrenOrganizations) => {
                const organizations = [organization, ...childrenOrganizations];
                const dataKey = 'projectFilterOrganization.items';
                const items = this.data.get(dataKey);
                this.data.splice(dataKey, [0, items.length, ...organizations]);
            });
    }

    // 选中/取消 责任单位
    toggleCheckProjectFilterOrganization(item?: IOrganization) {
        // 不传责任单位。不限
        if (!item) {
            this.clearProjectFilterOrganization();
            return;
        }
        // 选中->未选中。获取该组织的兄弟组织及父组织
        if (item._is_checked) {
            // 当前为一级组织
            if (ROOT_PARENT_ORG_NUMBER === item.parent_org_number) {
                this.clearProjectFilterOrganization();
                // 当前不是一级组织
            } else {
                Promise.all([
                    organizationService.getOrganization(item.parent_org_number),
                    organizationService.getChildrenOrganizations(item.parent_org_number),
                ]).then((results) => {
                    const organization = results[0];
                    organization._is_checked = true;
                    const childrenOrganizations = results[1];
                    const organizations = [organization, ...childrenOrganizations];
                    const dataKey = 'projectFilterOrganization.items';
                    const items = this.data.get(dataKey);
                    this.data.splice(dataKey, [0, items.length, ...organizations]);
                    this.fireProjectsParams();
                });
            }
            // 未选中->选中。获取该组织的子组织
        } else {
            item._is_checked = true;
            organizationService
                .getChildrenOrganizations(item.org_number)
                .then((childrenOrganizations) => {
                    const organizations = [item, ...childrenOrganizations];
                    const dataKey = 'projectFilterOrganization.items';
                    const items = this.data.get(dataKey);
                    this.data.splice(dataKey, [0, items.length, ...organizations]);
                    this.fireProjectsParams();
                });
        }
    }

    // 取消选中 责任单位
    clearProjectFilterOrganization() {
        organizationService.getChildrenOrganizations().then((organizations) => {
            const dataKey = 'projectFilterOrganization.items';
            const items = this.data.get(dataKey);
            this.data.splice(dataKey, [0, items.length, ...organizations]);
            this.fireProjectsParams();
        });
    }

    // 初始化创建单位
    initProjectFilterCreateOrganization(orgNumber?: string) {
        // 未选中，获取一级组织
        if (!orgNumber) {
            organizationService.getChildrenOrganizations().then((organizations) => {
                const dataKey = 'projectFilterCreateOrganization.items';
                const items = this.data.get(dataKey);
                this.data.splice(dataKey, [0, items.length, ...organizations]);
            });
            return;
        }
        // 已选中，获取选中的组织，及其子组织
        let organization: IOrganization;
        organizationService
            .getOrganization(orgNumber)
            .then((org) => {
                org._is_checked = true;
                organization = org;
                return organizationService.getChildrenOrganizations(organization.parent_org_number);
            })
            .then((childrenOrganizations) => {
                const organizations = [organization, ...childrenOrganizations];
                const dataKey = 'projectFilterCreateOrganization.items';
                const items = this.data.get(dataKey);
                this.data.splice(dataKey, [0, items.length, ...organizations]);
            });
    }

    // 选中/取消 创建单位
    toggleCheckProjectFilterCreateOrganization(item?: IOrganization) {
        // 不传创建单位。不限
        if (!item) {
            this.clearProjectFilterCreateOrganization();
            return;
        }
        // 选中->未选中。获取该组织的兄弟组织及父组织
        if (item._is_checked) {
            // 当前为一级组织
            if (ROOT_PARENT_ORG_NUMBER === item.parent_org_number) {
                this.clearProjectFilterCreateOrganization();
                // 当前不是一级组织
            } else {
                Promise.all([
                    organizationService.getOrganization(item.parent_org_number),
                    organizationService.getChildrenOrganizations(item.parent_org_number),
                ]).then((results) => {
                    const organization = results[0];
                    organization._is_checked = true;
                    const childrenOrganizations = results[1];
                    const organizations = [organization, ...childrenOrganizations];
                    const dataKey = 'projectFilterCreateOrganization.items';
                    const items = this.data.get(dataKey);
                    this.data.splice(dataKey, [0, items.length, ...organizations]);
                    this.fireProjectsParams();
                });
            }
            // 未选中->选中。获取该组织的子组织
        } else {
            item._is_checked = true;
            organizationService
                .getChildrenOrganizations(item.org_number)
                .then((childrenOrganizations) => {
                    const organizations = [item, ...childrenOrganizations];
                    const dataKey = 'projectFilterCreateOrganization.items';
                    const items = this.data.get(dataKey);
                    this.data.splice(dataKey, [0, items.length, ...organizations]);
                    this.fireProjectsParams();
                });
        }
    }

    // 取消选中 创建单位
    clearProjectFilterCreateOrganization() {
        organizationService.getChildrenOrganizations().then((organizations) => {
            const dataKey = 'projectFilterCreateOrganization.items';
            const items = this.data.get(dataKey);
            this.data.splice(dataKey, [0, items.length, ...organizations]);
            this.fireProjectsParams();
        });
    }

    search() {
        this.fireProjectsParams();
    }

    download() {
        this.fireProjectReportDownload();
    }

    clearQ() {
        this.data.set('q', '');
    }

    clearFilters() {
        this.data.set('q', '');
        this.data.set('from_date', '');
        this.data.set('to_date', '');
        this.data.set('order', EProjectsOrder.Status);
        this.toggleCheckProjectFilterOrganization();
        this.toggleCheckProjectFilterCreateOrganization();
        const projectFilters: IProjectFilters = this.data.get('projectFilters');
        _.forEach(projectFilters, (filter, key) => {
            _.forEach(filter.items, (item) => {
                item.is_checked = false;
            });
            const dataKey = `projectFilters.${key}.items`;
            this.data.splice(dataKey, [0, filter.items.length, ...filter.items]);
        });
        this.fireProjectsParams();
    }

    toggleShowMore() {
        this.data.set('isShowMore', !this.data.get('isShowMore'));
    }

    private getSearchParams() {
        const projectFilters: IProjectFilters = this.data.get('projectFilters');
        const projectFilterOrganization: IProjectFilterOrganization = this.data.get(
            'projectFilterOrganization'
        );
        const checkedOrganization = _.find(projectFilterOrganization.items, { _is_checked: true });
        const projectFilterCreateOrganization: IProjectFilterCreateOrganization = this.data.get(
            'projectFilterCreateOrganization'
        );
        const checkedCreateOrganization = _.find(projectFilterCreateOrganization.items, {
            _is_checked: true,
        });
        const params: IProjectsParams = {
            q: this.data.get('q'),
            from_date: this.data.get('from_date'),
            to_date: this.data.get('to_date'),
            organization: checkedOrganization && checkedOrganization.org_number, // 责任单位单独处理
            create_organization: checkedCreateOrganization && checkedCreateOrganization.org_number, // 创建单位单独处理
            order: this.data.get('order') || EProjectsOrder.Status, // 排序规则单独处理
            search_mode: this.data.get('searchMode'), // 查询模式
        };
        _.forEach(projectFilters, (filter, key) => {
            if ('organization' === key || 'create_organization' === key || 'order' === key) {
                // 责任单位、创建单位、排序规则 单独处理
                return;
            }
            const checkedItems = _.filter(filter.items, { is_checked: true });
            if (checkedItems.length) {
                params[key] = _.map(checkedItems, 'value');
            }
        });
        return params;
    }

    private fireProjectReportDownload() {
        const params: IProjectsParams = this.getSearchParams();
        this.dispatch('UI:project-filters-download-report', { params });
    }

    private fireProjectsParams() {
        const params: IProjectsParams = this.getSearchParams();
        this.dispatch('UI:project-filters-changed', { params });
    }

    // submitQ(event: any) {
    //     if (13 === event.keyCode) {
    //         // 回车
    //         this.search();
    //     }
    // }
}
